import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components/macro";
import _ from "lodash";
import { useLocation, useHistory } from "react-router-dom";
import {
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from "@material-ui/core";
import { enqueueSnackbar } from "notistack";
import Loading from "../components/Loading";
import MyRadonInterface from "../../integration/internal/MyRadon/MyRadonInterface";
import moment from "moment";
import { createMuiTheme } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import AnalysisInProgressDialog from "../../components/AnalysisInProgressDialog";
import { FAILURE, NO_ELEMENTS_MATCHED, SUCCESS } from "../../constants/resultCodes";
import * as types from "../../constants";

const theme = createMuiTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h6: {
      fontSize: "16px",
    },
  },
});

const Container = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 0 0 0 0;
`;

const StyledTableCell = styled(TableCell)`
  padding: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ theme }) => css`
    font-size: 12px;
    @media (max-width: 400px) {
      font-size: 11px;
    }
    @media (min-width: 400px) and (max-width: 600px) {
      font-size: 12px;
    }
    @media (min-width: 600px) {
      font-size: 14px;
    }
  `}
`;

const StyledTableRow = styled(TableRow)`
  & > td {
    font-size: inherit;
  }

  ${({ theme }) => css`
    font-size: 11px;
    @media (max-width: 400px) {
      font-size: 11px;
    }
    @media (min-width: 400px) and (max-width: 600px) {
      font-size: 12px;
    }
    @media (min-width: 600px) {
      font-size: 14px;
    }
  `}

  height: 50px;
`;

const FirstColumnCell = styled(StyledTableCell)`
  color: #333;
`;

const SecondColumnCell = styled(StyledTableCell)`
  color: #333;
`;

const ThirdColumnCell = styled(StyledTableCell)`
  color: #333;
`;

const StyledTableHeadCell = styled(StyledTableCell)`
  font-weight: bold;
`;

const StyledTableSortLabel = styled(TableSortLabel)`
  .MuiTableSortLabel-icon {
    ${({ theme }) => css`
      font-size: 20px;

      @media (max-width: 400px) {
        font-size: 12px;
      }
      @media (min-width: 400px) and (max-width: 600px) {
        font-size: 16px;
      }
      @media (min-width: 600px) {
        font-size: 20px;
      }
    `}
  }
`;

const ReportList = () => {
  const location = useLocation();
  const history = useHistory();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: "requested", direction: "desc" });
  const [open, setOpen] = useState(false);
  const [span, setSpan] = useState(null);
  const [collected, setCollected] = useState(null);
  const [serialNumber, setSerialNumber] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);

  let { email, reportId } = location.state || {};
  email = email || localStorage.getItem("0xaccountId");

  const handleClose = () => {
    setOpen(false);
    setSpan(null);
    setCollected(null);
    setSerialNumber(null);
    setSelectedReportId(null);
  };

  const handleCancel = async () => {
    if (!serialNumber || !selectedReportId) {
      enqueueSnackbar("Missing serial number or report ID", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      const res = await MyRadonInterface.cancelAnalysis(serialNumber, selectedReportId);
      if (res.resultcode === SUCCESS) {
        enqueueSnackbar("Analysis cancelled successfully", { variant: "success" });
        // Refresh the table data after cancellation
        const updatedData = await MyRadonInterface.getMyRadonList(email);
        setTableData(_.orderBy(_.get(updatedData, "data", []), [sortConfig.key], [sortConfig.direction]));
      } else {
        throw new Error("Cancellation failed");
      }
    } catch (error) {
      console.error("Error cancelling radon analysis:", error);
      enqueueSnackbar("Error cancelling radon analysis", { variant: "error" });
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  useEffect(() => {
    const loadReportList = async () => {
      try {
        setLoading(true);
        const res = await MyRadonInterface.getMyRadonList(email);
        const data = _.get(res, "data", []);
        if (!data.length) {
          enqueueSnackbar("No reports found", { variant: "info" });
        }
        setTableData(_.orderBy(data, ["requested"], ["desc"]));
      } catch (error) {
        console.error("Error fetching report details:", error);
        enqueueSnackbar(`Error fetching report details: ${error.message}`, { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    loadReportList();
  }, [email]);

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
    setTableData((prevData) => _.orderBy(prevData, [key], [direction]));
  };

  const handleRowClick = (rptId, processed, sn, span, collected) => {
    if (processed === "Y") {
      history.replace("/report", { email, reportId: rptId });
    } else if (processed === "N") {
      setSpan(span);
      setCollected(collected);
      setSerialNumber(sn);
      setSelectedReportId(rptId);
      setOpen(true);
    }
  };

  if (loading) return <Loading />;

  return (
    <Container>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <AnalysisInProgressDialog
          handleClose={handleClose}
          handleCancel={handleCancel}
          reportId={selectedReportId}
          span={span}
          collected={collected}
        />
      </Dialog>
      {tableData.length > 0 ? (
        <Card
          style={{
            background: "white",
            minHeight: "calc(83vh)",
          }}
        >
          <CardContent
            style={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              paddingTop: 3,
              paddingBottom: 3,
              paddingLeft: 0,
              paddingRight: 0,
              margin: 0,
            }}
          >
            <TableContainer component={Paper} style={{ flexGrow: 1 }}>
              <Table style={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <StyledTableHeadCell align="center" style={{ width: "30%" }}>
                      <StyledTableSortLabel
                        active={sortConfig.key === "device_name"}
                        direction={sortConfig.direction}
                        onClick={() => handleSort("device_name")}
                      >
                        Device
                      </StyledTableSortLabel>
                    </StyledTableHeadCell>
                    <StyledTableHeadCell align="center" style={{ width: "44%" }}>
                      <StyledTableSortLabel
                        active={sortConfig.key === "requested"}
                        direction={sortConfig.direction}
                        onClick={() => handleSort("requested")}
                      >
                        Requested
                      </StyledTableSortLabel>
                    </StyledTableHeadCell>
                    <StyledTableHeadCell align="center" style={{ width: "26%" }}>
                      <StyledTableSortLabel
                        active={sortConfig.key === "processed"}
                        direction={sortConfig.direction}
                        onClick={() => handleSort("processed")}
                      >
                        Status
                      </StyledTableSortLabel>
                    </StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.map(({ rpt_id, device_name, requested, processed, sn, span, collected }) => (
                    <StyledTableRow
                      key={rpt_id}
                      hover
                      onClick={() => handleRowClick(rpt_id, processed, sn, span, collected)}
                    >
                      <FirstColumnCell align="center">{device_name}</FirstColumnCell>
                      <SecondColumnCell align="center">
                        {moment.unix(requested).format("MM/DD/YY HH:mm:ss")}
                      </SecondColumnCell>
                      <ThirdColumnCell align="center">
                        {processed === "Y" ? "Completed" : processed === "N" ? "In Progress" : "Cancelled"}
                      </ThirdColumnCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" style={{ textAlign: "center", color: "#808080", fontSize: "13px" }}>
              {email ? "There are no completed reports generated from this email account." : "There's no passed email. Please try again."}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default ReportList;
